import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BellRing, CheckCheck, Loader2, UserRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { API_URL, fmtDT } from './pendShared';

export const PendenciaBlockingPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try { const { data } = await axios.get(`${API_URL}/pendencias/bloqueantes`); setRows(Array.isArray(data) ? data : []); } catch { /* silent */ }
  }, [user]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { setNote(''); }, [rows[0]?.id]);

  if (!user || !rows.length) return null;
  const p = rows[0];
  const late = p.status === 'em_atraso';

  const act = async () => {
    setBusy(true);
    try {
      if (late) await axios.post(`${API_URL}/pendencias/${p.id}/justificar`, { note: note.trim() });
      else await axios.post(`${API_URL}/pendencias/${p.id}/lido`);
      toast.success(late ? 'Justificação enviada' : 'Marcado como lido — tem 24h para dar seguimento');
      setRows((r) => r.slice(1));
      window.dispatchEvent(new Event('pendencias-changed'));
    } catch (e) { toast.error((typeof e.response?.data?.detail === 'string' && e.response.data.detail) || 'Erro'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="pend-blocking-popup">
      <div className="w-full max-w-xl rounded-2xl overflow-hidden bg-white shadow-2xl">
        <div className={`${late ? 'bg-rose-700' : 'bg-[#2D3E6F]'} px-6 py-4 text-white flex items-center gap-3`}>
          <BellRing className="w-6 h-6" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-white/75 font-bold">{late ? 'Prazo de 24h terminado' : 'Pendência para resolver'}</p>
            <p className="font-bold">De: {p.sender_name} · {fmtDT(p.created_at)}</p>
          </div>
          {rows.length > 1 && <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded-full">1 de {rows.length}</span>}
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-slate-800 whitespace-pre-wrap" data-testid="pend-popup-message">{p.message}</div>
          {p.nif && (
            <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => navigate(`/clientes/360?nif=${encodeURIComponent(p.nif)}`)} data-testid="pend-popup-open-client">
              <UserRound className="w-4 h-4" />Abrir cliente
            </Button>
          )}
          {late ? (
            <>
              <p className="text-sm font-semibold text-rose-700">Não deu seguimento dentro das 24h. Justifique (obrigatório). A situação passa a "Pendente de resolução" urgente.</p>
              <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Porque não foi tratado? (mín. 10 caracteres)" data-testid="pend-popup-justify" autoFocus />
            </>
          ) : (
            <p className="text-sm text-slate-600">Ao marcar como lido, o gestor é avisado e começa a contagem de <b>24h</b> (desde o envio) para dar seguimento ou concluir.</p>
          )}
          <Button onClick={act} disabled={busy || (late && note.trim().length < 10)} className={`w-full h-11 gap-2 ${late ? 'bg-rose-700 hover:bg-rose-800' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="pend-popup-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}{late ? 'Enviar justificação' : 'Li o alerta'}
          </Button>
        </div>
      </div>
    </div>
  );
};
